import {RecordPreview} from './record-preview.js';
import { defineComponent, h, ref, computed, shallowRef, onMounted } from 'vue';
import type { EvidenceTrace, EvidenceSource } from '@knowyet/sdk/evidence';
import { GraphScene, kindLabels, type SceneNode, type SceneEdge } from './graph-scene.js';
import { selectNodeSources } from './trace-source-selection.js';
export const TracePanel = defineComponent({
  props: {
    loadArtifact: { type: Function },
    trace: { type: Object as () => EvidenceTrace, required: true },
    loadCitation: { type: Function as unknown as () => (id: string, offset?: number) => Promise<EvidenceTrace>, required: true },
    loadGraph: { type: Function as unknown as () => () => Promise<EvidenceTrace>, required: true },
    loadPage: { type: Function as unknown as () => (offset: number) => Promise<EvidenceTrace>, required: true },
  },
  emits: ['source', 'close'],
  setup(props, { emit }) {
    const focus = ref(''), graphLoaded = ref(props.trace.view==='graph'), graphLoading = ref(false);
    const graph = shallowRef(props.trace), citationPage = ref<EvidenceTrace['source_page']>();
    onMounted(async()=>{if(!graphLoaded.value){graphLoading.value=true;try{graph.value=await props.loadGraph();graphLoaded.value=true;}catch{loadError.value='关系加载失败，请重试。';}finally{graphLoading.value=false;}}});
    const citationSources = ref<EvidenceSource[]>([]), loading = ref(false), loadError = ref('');
    const sourceScope = ref<'citation' | 'evidence'>('citation');
    const nodeSources = (sources: EvidenceSource[], id: string) => {
      const selected = graph.value.nodes.find(n => n.id === id);
      const blob = (selected?.body as {blob?: {id?:string}} | undefined)?.blob?.id;
      return selectNodeSources(sources, id, blob);
    };
    const records = computed(() => {
      const found=new Map<string,any>();
      for(const node of graph.value.nodes){const body=(node.body??node) as any,r=body.result?.records;if(r?.download?.id)found.set(r.download.id,r);}
      return [...found.values()];
    });
    const nodes = computed<SceneNode[]>(() =>
      graph.value.nodes.map((n) => {
        const b = (n.body || n) as Record<string, any>,
          id = String(n.id);
        const kind =
          n.layer === 'H'
            ? String(n.kind)
            : id.startsWith('source-version:')
              ? 'source'
              : id.startsWith('doc:')
                ? 'document'
                : id.startsWith('unit:')
                  ? 'unit'
                  : id.startsWith('chunk:')
                    ? 'chunk'
                    : 'passage';
        return {
          id,
          kind,
          label: String(
            b.text && n.layer === 'H'
              ? b.text
              : b.quote || b.query || b.claim ||
                  b.explanation ||
                  b.name ||
                  b.index_text ||
                  b.title ||
                  b.path ||
                  b.summary ||
                  kindLabels[kind] ||
                  '原文依据',
          ),
        };
      }),
    );
    const edges = computed<SceneEdge[]>(() =>
      graph.value.edges.map((e) => ({ from: String(e.from), to: String(e.to), kind: String(e.kind) })),
    );
    const choose = async (id: string) => {
      focus.value = id;
      citationSources.value = []; citationPage.value = undefined; loadError.value = ''; loading.value = false;
      const selected = graph.value.nodes.find(n => n.id === id);
      if (selected?.kind === 'retrieval') return;
      if (selected?.kind === 'citation') {
        sourceScope.value = 'citation';
        loading.value = true;
        try {
          // The kernel owns exact span projection. Never infer citation boxes
          // from another quote that happens to use the same passage or file.
          const result = await props.loadCitation(id);
          const sources = result.sources;
          if (focus.value !== id) return;
          citationPage.value = result.source_page;
          citationSources.value = sources;
          if (sources.length === 1) emit('source', sources[0]);
        } catch { if (focus.value === id) loadError.value = '引文暂时未能加载，请再次点击。'; }
        finally { if (focus.value === id) loading.value = false; }
        return;
      }
      sourceScope.value = 'evidence';
      citationSources.value = nodeSources(props.trace.sources, id);
      citationPage.value = props.trace.source_page;
      if (citationSources.value.length === 1 && !citationPage.value?.has_more)
        emit('source', citationSources.value[0]);
    };
    return () =>
      h('section', { class: 'card trace', 'data-testid': 'trace' }, [
        h('div', { class: 'section-title' }, [
          h('h3', '这句话的依据'),
          h('button', { type: 'button', onClick: () => emit('close') }, '收起溯源'),
        ]),
        props.trace.freshness === 'needs_review'
          ? h('p', { class: 'notice' }, '材料已经更新，此回答引用之前保存的版本。')
          : null,
        h('p', { class: 'muted' }, '仅显示所选回答的依据。从回答沿连线查看支撑依据；点击黄色引文节点打开对应原件位置。'),
        ...records.value.map(r=>h(RecordPreview,{records:r,loadArtifact:props.loadArtifact??(()=>Promise.reject(Error('unavailable')))})),
        h('div', { class: 'trace-graph' }, [
          graphLoading.value ? h('p',{role:'status'},'正在加载关系…') : null,
          graphLoaded.value ? h(GraphScene, {
            nodes: nodes.value,
            edges: edges.value,
            focus: focus.value,
            trace: true,
            onSelect: choose,
            onExpand: choose,
          }) : null,
          focus.value
            ? h('p', { class: 'trace-node-text' }, nodes.value.find((n) => n.id === focus.value)?.label)
            : null,

          loading.value ? h('p', {role:'status'}, '正在定位引文…') : null,
          loadError.value ? h('p', {role:'alert'}, loadError.value) : null,
          ...citationSources.value.map((s, i) => h('button', {type:'button', class:'source',
            'data-selected-citation': i, onClick:()=>emit('source',s)}, [h('strong',s.file), h('blockquote',s.text)])),
          citationPage.value?.has_more ? h('button',{type:'button',disabled:loading.value,onClick:async()=>{
            loading.value=true; const id=focus.value;
            try { const next = sourceScope.value === 'citation'
              ? await props.loadCitation(id,citationPage.value!.next_offset)
              : await props.loadPage(citationPage.value!.next_offset);
              if(focus.value===id){citationSources.value=[...citationSources.value,...(sourceScope.value === 'citation' ? next.sources : nodeSources(next.sources,id))];citationPage.value=next.source_page;} }
            catch { loadError.value='来源加载失败，请重试。'; } finally { loading.value=false; }
          }},sourceScope.value === 'citation' ? '加载更多引文来源' : '继续查找该节点来源') : null,
        ]),
        ...props.trace.unlocated.map((s) => h('p', { class: 'muted' }, `${s.file}：原始空单元格没有文字定位框。`)),
      ]);
  },
});
